import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import { useDispatch } from "react-redux";
import { MutableRefObject, useCallback, useRef } from "react";
import { useDebounceProcessing } from "@hooks/useDebounceProcessing";
import { setTalking } from "@store/avatarSlice";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { EventType } from "@models/audio-message";
import {
  pushToTalkAbortStreamMessage,
  pushToTalkEndStreamMessage,
} from "@models/audio-message-stream";
import { ConversationPacing } from "@models/conversation-settings";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";

const useStreaming = ({
  sessionId,
  avatar,
  ttsEvictionMs,
}: {
  sessionId: string;
  avatar: MutableRefObject<AzureAvatarService | null>;
  ttsEvictionMs: number;
}): {
  sendMessage: (
    audio?: ArrayBufferLike,
    photoId?: string,
    eventType?: EventType,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  /** PTT streaming: notify server to finalize STT/LLM after mic stop (end talk). */
  signalPushToTalkEnd: (
    photoId?: string | null,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  /** PTT streaming: abort in-flight utterance (no LLM turn) after mic aborted. */
  signalPushToTalkAbort: (
    photoId?: string | null,
    conversationPacing?: ConversationPacing,
  ) => Promise<void>;
  interruptPlayback: () => Promise<void>;
  suppressGalleryAssistantStream: () => void;
} => {
  const dispatch = useDispatch();
  const { vadBargeInEnabled, voiceInputMode } = useCompanionConversation();
  const speakQueueSize = useRef(0);
  const { setValue: setProcessing } = useDebounceProcessing();

  const speak = useCallback(
    async (text: string) => {
      if (!avatar.current?.running) {
        return;
      }
      speakQueueSize.current = speakQueueSize.current + 1;
      setProcessing(true, true);
      dispatch(setTalking(true));
      try {
        await avatar.current.speak(text);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setTalking(false));
        // `interruptPlayback` zeroes the queue while a speak may still finish; avoid negative count
        // or the next speak leaves processing true forever (see `!(-1)`).
        speakQueueSize.current = Math.max(0, speakQueueSize.current - 1);
        if (!speakQueueSize.current) {
          setProcessing(false, true);
        }
      }
    },
    [dispatch, setProcessing, avatar],
  );

  const { send, suppressGalleryAssistantStream } = useCompanionStreamingService(
    speak,
    true,
    ttsEvictionMs,
  );

  const interruptPlayback = useCallback(async () => {
    speakQueueSize.current = 0;
    setProcessing(false, true);
    dispatch(setTalking(false));
    await avatar.current?.stopSpeaking();
  }, [avatar, dispatch, setProcessing]);

  const sendMessage = useCallback(
    async (
      audio?: ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
      conversationPacing?: ConversationPacing,
    ): Promise<void> => {
      try {
        await send({
          audio: audio || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
          conversationPacing,
          vadBargeInEnabled,
          voiceInputMode,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [sessionId, send, vadBargeInEnabled, voiceInputMode],
  );

  const signalPushToTalkEnd = useCallback(
    async (
      photoId?: string | null,
      conversationPacingArg?: ConversationPacing,
    ) => {
      if (voiceInputMode !== "push_to_talk") {
        return;
      }
      try {
        await send(
          pushToTalkEndStreamMessage({
            sessionId,
            photoId: photoId ?? null,
            conversationPacing: conversationPacingArg,
            vadBargeInEnabled,
            voiceInputMode,
          }),
        );
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  const signalPushToTalkAbort = useCallback(
    async (
      photoId?: string | null,
      conversationPacingArg?: ConversationPacing,
    ) => {
      if (voiceInputMode !== "push_to_talk") {
        return;
      }
      try {
        await send(
          pushToTalkAbortStreamMessage({
            sessionId,
            photoId: photoId ?? null,
            conversationPacing: conversationPacingArg,
            vadBargeInEnabled,
            voiceInputMode,
          }),
        );
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId, vadBargeInEnabled, voiceInputMode],
  );

  return {
    sendMessage,
    signalPushToTalkEnd,
    signalPushToTalkAbort,
    interruptPlayback,
    suppressGalleryAssistantStream,
  };
};

export default useStreaming;
